import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { layoutWall, wallMetrics, panAnchor, steer, bulge } from '../../utils/filmWall.js';
import styles from './FilmWall.module.css';

// ─── Film wall ────────────────────────────────────────────────────────────────
// Desktop: a mosaic bigger than the screen, bowed toward the viewer. The cursor
// steers the view (screen edge = wall edge, screen centre = first film) and every
// tile re-tilts as it moves across the lens. Hovering a tile plays its 4s
// preview loop; clicking hands the tile element up so the pop-up can grow out
// of it.
//
// Transforms are written straight to the DOM from one rAF loop — nothing here
// re-renders while panning.
// ─────────────────────────────────────────────────────────────────────────────

const EASE = 0.075;        // pan smoothing per frame

const clamp01 = (v) => Math.min(Math.max(v, 0), 1);

const Tile = memo(function Tile({ item, delay, onOpen, onHover, onFocusTile, register }) {
  const { film, clone } = item;
  const [live, setLive] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const enter = () => {
    onHover(film);
    if (!film.preview) return;
    if (!live) setLive(true);
    else videoRef.current?.play().catch(() => {});
  };
  const leave = () => {
    onHover(null);
    const v = videoRef.current;
    if (v) { v.pause(); setPlaying(false); }
  };

  return (
    <button
      ref={(el) => register(item.key, el)}
      type="button"
      className={styles.tile}
      style={{ left: item.x, top: item.y, width: item.w, height: item.h }}
      data-cursor="play"
      data-film={film.id}
      data-clone={clone || undefined}
      tabIndex={clone ? -1 : 0}
      aria-hidden={clone || undefined}
      aria-label={`${film.title} — ${film.sub}`}
      onPointerEnter={enter}
      onPointerLeave={leave}
      onFocus={() => { onHover(film); onFocusTile(item); }}
      onBlur={() => onHover(null)}
      onClick={(e) => { leave(); onOpen(film, e.currentTarget); }}
    >
      <span className={styles.tileInner} style={{ animationDelay: `${delay}ms` }}>
        <img className={styles.tileMedia} src={film.poster} alt="" draggable="false" decoding="async" />
        {live && (
          <video
            ref={videoRef}
            className={`${styles.tileMedia} ${playing ? styles.tileVideoOn : styles.tileVideo}`}
            src={film.preview}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            onPlaying={() => setPlaying(true)}
          />
        )}
        <span className={styles.tileShade} />
      </span>
    </button>
  );
});

export default function FilmWall({ films, onOpen, onHover, frozen }) {
  const viewportRef = useRef(null);
  const wallRef = useRef(null);
  const tiles = useRef(new Map());
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  const layout = useMemo(() => layoutWall(films, wallMetrics(vp.w, vp.h)), [films, vp]);

  // where the view rests: the first film in the list, dead centre
  const anchor = useMemo(() => panAnchor(layout, vp.w, vp.h), [layout, vp]);
  const range = useMemo(() => ({ x: anchor.rx, y: anchor.ry }), [anchor]);

  // pan state lives in refs — the loop owns it
  const pan = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const raf = useRef(0);
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  const register = useCallback((key, el) => {
    if (el) tiles.current.set(key, el); else tiles.current.delete(key);
  }, []);

  const paint = useCallback(() => {
    const p = pan.current;
    const wall = wallRef.current;
    if (!wall) return;
    wall.style.transform = `translate3d(${(-p.x).toFixed(2)}px, ${(-p.y).toFixed(2)}px, 0)`;
    const cx = vp.w / 2, cy = vp.h / 2;
    for (const item of layout.items) {
      const el = tiles.current.get(item.key);
      if (!el) continue;
      const nx = (item.x + item.w / 2 - p.x - cx) / cx;
      const ny = (item.y + item.h / 2 - p.y - cy) / cy;
      // far off-screen tiles don't need a fresh transform every frame
      if (Math.abs(nx) > 1.9 || Math.abs(ny) > 2.2) continue;
      el.style.transform = bulge(nx, ny);
    }
  }, [layout, vp]);

  const tick = useCallback(() => {
    const p = pan.current;
    p.x += (p.tx - p.x) * EASE;
    p.y += (p.ty - p.y) * EASE;
    paint();
    if (Math.abs(p.tx - p.x) > 0.1 || Math.abs(p.ty - p.y) > 0.1) {
      raf.current = requestAnimationFrame(tick);
    } else {
      p.x = p.tx; p.y = p.ty;
      paint();
      raf.current = 0;
    }
  }, [paint]);

  const wake = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(tick);
  }, [tick]);

  // New layout (resize, filter, shuffle): start on the lead film, paint once.
  useEffect(() => {
    const p = pan.current;
    p.x = p.tx = anchor.x;
    p.y = p.ty = anchor.y;
    cancelAnimationFrame(raf.current);
    raf.current = 0;
    paint();
  }, [layout, anchor, paint]);

  useEffect(() => {
    let pending = 0;
    const onResize = () => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(() => setVp({ w: window.innerWidth, h: window.innerHeight }));
    };
    const onMove = (e) => {
      if (frozenRef.current) return;
      // a touch of gain so the wall's edges arrive before the cursor hits the bezel
      const gx = clamp01((e.clientX / vp.w - 0.5) * 1.18 + 0.5);
      const gy = clamp01((e.clientY / vp.h - 0.5) * 1.18 + 0.5);
      pan.current.tx = steer(gx, anchor.x, range.x);
      pan.current.ty = steer(gy, anchor.y, range.y);
      wake();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(pending);
    };
  }, [vp, anchor, range, wake]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  // Keyboard users: bring the focused tile to the middle of the lens.
  const onFocusTile = useCallback((item) => {
    const p = pan.current;
    p.tx = Math.min(Math.max(item.x + item.w / 2 - vp.w / 2, 0), range.x);
    p.ty = Math.min(Math.max(item.y + item.h / 2 - vp.h / 2, 0), range.y);
    wake();
  }, [vp, range, wake]);

  // Tiles deal in from the centre outward.
  const delays = useMemo(() => {
    const cx = layout.width / 2, cy = layout.height / 2;
    const far = Math.hypot(cx, cy) || 1;
    return layout.items.map((it) =>
      Math.round((Math.hypot(it.x + it.w / 2 - cx, it.y + it.h / 2 - cy) / far) * 520));
  }, [layout]);

  return (
    <div
      ref={viewportRef}
      className={`${styles.viewport} ${frozen ? styles.frozen : ''}`}
    >
      <div
        ref={wallRef}
        className={styles.wall}
        style={{ width: layout.width, height: layout.height }}
      >
        {layout.items.map((item, i) => (
          <Tile
            key={item.key}
            item={item}
            delay={delays[i]}
            onOpen={onOpen}
            onHover={onHover}
            onFocusTile={onFocusTile}
            register={register}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Phone / reduced motion ───────────────────────────────────────────────────
// No lens, no steering: a masonry grid that scrolls like a page. Each film drops
// into the currently shortest column, so the list still reads left to right
// (CSS columns would fill the whole first column before starting the second).
function useColumns() {
  const count = () => (window.innerWidth >= 620 ? 3 : 2);
  const [cols, setCols] = useState(count);
  useEffect(() => {
    const onResize = () => setCols(count());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return cols;
}

export function FilmGrid({ films, onOpen }) {
  const cols = useColumns();
  const columns = useMemo(() => {
    const out = Array.from({ length: cols }, () => ({ h: 0, films: [] }));
    films.forEach((film) => {
      const col = out.reduce((a, b) => (b.h < a.h - 0.01 ? b : a));
      col.films.push(film);
      col.h += film.height / film.width + 0.18;   // + caption
    });
    return out;
  }, [films, cols]);

  return (
    <div className={styles.grid}>
      {columns.map((col, c) => (
        <div key={c} className={styles.gridCol}>
          {col.films.map((film) => (
            <button
              key={film.id}
              type="button"
              className={styles.gridTile}
              data-film={film.id}
              aria-label={`${film.title} — ${film.sub}`}
              onClick={(e) => onOpen(film, e.currentTarget)}
            >
              <span className={styles.gridMedia} style={{ aspectRatio: `${film.width} / ${film.height}` }}>
                <img src={film.poster} alt="" loading="lazy" decoding="async" draggable="false" />
              </span>
              <span className={styles.gridTitle}>{film.title}</span>
              <span className={styles.gridSub}>{film.sub}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
